import { PROJECT_AREA_LINE_COLOR } from '~/colors';

/**
 * Printed page box — single source of truth for every full-page section.
 *
 * These are deliberately smaller than the theoretical Letter content box (8.5in x 11in minus twice
 * the `@page` margin). Sizing sections to the theoretical box breaks Safari: WebKit ignores
 * `@page { size }`, only partially honours `@page { margin }`, and the printable area shrinks
 * further by whatever the print dialog reserves. A section even slightly taller than the real
 * printable area produces a mostly-blank sheet after every page, which is worse than unused margin.
 *
 * The height budget assumes the worst realistic Safari case: 11in, less ~0.5in of imageable-area
 * margin, less ~0.5in for the headers/footers Safari reserves when that option is on — about 10in —
 * and then keeps ~0.8in of slack under that. The cost is a wider bottom gap above the footer in
 * Chrome, whose printable area is genuinely larger. Do not raise these to reclaim that space
 * without testing Safari with headers/footers enabled.
 */
export const PRINT_PAGE_MARGIN = '0.28in';
export const PRINT_PAGE_CONTENT_WIDTH = '7.5in';
export const PRINT_PAGE_CONTENT_HEIGHT = '9.2in';

/**
 * Styles for the injected print overlay host: hides it off-screen on screen, and takes over the
 * page for printing (hiding the app root).
 *
 * The overlay must stay **block flow** while printing. Each logical page is a child section that
 * forces a page break before itself, and WebKit does not fragment flex containers in paged media —
 * as flex items those sections lose their forced breaks entirely and overflow is clipped rather
 * than carried to a new sheet. Blink and Gecko do fragment flex, which is why making this a flex
 * column only ever broke Safari. Horizontal centring comes from `margin: 0 auto` on the sections.
 */
export function buildPermittingPrintHostStyles(overlayId: string): string {
  return `
    #${overlayId} {
      position: fixed;
      left: -10000px;
      top: 0;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
    @media print {
      /*
       * The app's global rules (packages/client/index.css) size the document to the window for the
       * full-screen map: html gets overflow: hidden + max-height: 100vh, body gets width: 100vw and
       * height: 100vh. Those survive into print, and Chrome and Safari disagree about what a
       * viewport unit means on a printed page: Chrome resolves vw/vh against the page box, Safari
       * against the browser window. In Safari the document therefore lays out at window width
       * inside a Letter sheet, so the whole thing is shrunk to fit and clipped to one window height
       * — the content collapses into a corner and the rest of the sheet is blank.
       *
       * So every dimension and clip the app sets has to be neutralised here, not just the ones that
       * happen to be inherited. These declarations must also out-rank Tailwind's !important
       * utilities (h-screen! / w-screen!); they do because this stylesheet is injected into <body>
       * and therefore comes later in document order at equal specificity.
       */
      html,
      body {
        margin: 0 !important;
        padding: 0 !important;
        position: static !important;
        width: auto !important;
        min-width: 0 !important;
        max-width: none !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
        background: #fff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      #root {
        display: none !important;
      }
      #${overlayId} {
        position: static !important;
        left: auto !important;
        top: auto !important;
        width: 100% !important;
        min-height: ${PRINT_PAGE_CONTENT_HEIGHT} !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        clip: auto !important;
        white-space: normal !important;
        display: block !important;
        box-sizing: border-box !important;
      }
      /* Pylon mounts outside #root; hide launcher + chat for permit print preview */
      [class*="PylonChat"],
      iframe[src*="widget.usepylon.com"] {
        display: none !important;
        visibility: hidden !important;
      }
    }
  `;
}

export function buildPermittingPrintContentStyles(overlayId: string): string {
  return `
    @import url('https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600&display=swap');

    #${overlayId} {
      box-sizing: border-box;
      font-family: "Noto Sans", system-ui, sans-serif;
      color: #1d1e20;
      line-height: 1.4;
      padding: 0;
      background: #fff;
    }
    #${overlayId} .letter {
      box-sizing: border-box;
      max-width: 8.5in;
      margin: 0 auto;
      border: none;
      padding: 14px 18px 12px;
      background: #fff;
    }
    #${overlayId} .print-form-card {
      box-sizing: border-box;
      width: 100%;
      border: 1px solid #d2d4d7;
      border-radius: 4px;
      background: #fff;
      padding: 16px;
      box-shadow: 0 1px 2px rgba(29, 30, 32, 0.2), 0 1px 3px rgba(29, 30, 32, 0.15);
    }
    #${overlayId} .print-permit-area-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #d2d4d7;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    #${overlayId} .print-subsection-title {
      margin: 0;
      font-size: 10pt;
      font-weight: 600;
      color: #243442;
      line-height: 1.3;
    }
    #${overlayId} .print-subsection-hint {
      margin: 0;
      font-size: 8.5pt;
      color: #6e7376;
      line-height: 1.35;
    }
    #${overlayId} .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px 14px;
      align-items: start;
    }
    #${overlayId} .span-2 { grid-column: span 2; }
    #${overlayId} .span-3 { grid-column: span 3; }
    #${overlayId} .print-field-block-spacer {
      grid-column: span 3;
      height: 6px;
      min-height: 6px; 
    }
    #${overlayId} .doc-title {
      grid-column: span 3;
      margin: 0;
      font-size: 10pt;
      font-weight: 600;
      color: #243442;
    }
    #${overlayId} .section-title {
      grid-column: span 3;
      margin: 0;
      padding: 0;
      font-size: 9.5pt;
      font-weight: 600;
      color: #243442;
    }
    #${overlayId} .field-group {
      min-width: 0;
    }
    #${overlayId} .permitting-construction-block {
      display: flex;
      flex-direction: column;
      gap: 0px;
      grid-column: span 3;
    }
    #${overlayId} .permitting-construction-row-rep-contractor,
    #${overlayId} .permitting-project-work-type-row {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px 14px;
      align-items: start;
    }
    #${overlayId} .permitting-project-work-type-row {
      grid-column: span 3;
    }
    #${overlayId} .permitting-construction-row-third {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px 14px;
      align-items: start;
    }
    #${overlayId} .permitting-construction-spacer {
      min-width: 0;
    }
    #${overlayId} .contractors-print {
      grid-column: span 3;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    #${overlayId} .contractor-row {
      display: grid;
      grid-template-columns: 0.8fr 1.2fr 1.2fr 1.4fr 1fr;
      gap: 4px 10px;
      align-items: end;
      padding: 4px 6px;
      border: 1px solid #e3e5e6;
      border-radius: 4px;
    }
    #${overlayId} .contractor-cell {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    #${overlayId} .contractor-cell-label {
      font-size: 7pt;
      font-weight: 500;
      color: #6e7376;
    }
    #${overlayId} .contractor-cell-value {
      font-size: 8.5pt;
      color: #1d1e20;
      word-break: break-word;
    }
    #${overlayId} .contractor-empty {
      font-size: 9pt;
      color: #6e7376;
    }
    #${overlayId} .field-label {
      font-size: 8.5pt;
      font-weight: 500;
      color: #393b3d;
      margin-bottom: 4px;
    }
    #${overlayId} .field-box {
      box-sizing: border-box;
      min-height: 22px;
      padding: 4px 8px;
      font-size: 9pt;
      color: #1d1e20;
      background: #f4f5f5;
      border: 1px solid #d2d4d7;
      border-radius: 4px;
      word-break: break-word;
    }
    /* Work Description: preserve the textarea's line breaks and grow to fit the full text. */
    #${overlayId} .field-box-multiline {
      min-height: 44px;
      white-space: pre-wrap;
    }
    #${overlayId} .radio-line {
      display: flex;
      flex-wrap: wrap;
      gap: 10px 14px;
      align-items: center;
      padding: 4px 0;
    }
    #${overlayId} .radio-item {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 8.5pt;
      color: #393b3d;
    }
    #${overlayId} .radio-dot {
      box-sizing: border-box;
      width: 9px;
      height: 9px;
      border-radius: 50%;
      border: 1px solid #393b3d;
      flex-shrink: 0;
    }
    #${overlayId} .radio-dot.on {
      background: #243442;
      border-color: #243442;
    }
    #${overlayId} .map-section {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      margin-top: 0;
      width: 100%;
      box-sizing: border-box;
    }
    #${overlayId} .map-frame {
      position: relative;
      box-sizing: border-box;
      width: 100%;
      height: auto;
      border: 1px solid #d2d4d7;
      border-radius: 4px;
      overflow: hidden;
      background: #fff;
      line-height: 0;
    }
    #${overlayId} .map-frame:has(.map-placeholder) {
      min-height: 120px;
      line-height: 1.4;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #${overlayId} .map-north-overlay {
      position: absolute;
      left: 8px;
      top: 8px;
      z-index: 2;
      padding: 2px 4px;
      background: rgba(255, 255, 255, 0.94);
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(29, 30, 32, 0.12);
      line-height: 0;
    }
    #${overlayId} .permitting-north-arrow-host {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #${overlayId} .map-north-fallback {
      display: none;
    }
    #${overlayId} .map-legend-below {
      box-sizing: border-box;
      width: 100%;
      margin-top: 8px;
      padding: 8px 10px;
      background: #fff;
      border: 1px solid #d2d4d7;
      border-radius: 4px;
      line-height: 1.25;
      text-align: left;
    }
    #${overlayId} .map-legend-fallback {
      display: none;
    }
    #${overlayId} .map-scale-overlay {
      position: absolute;
      right: 10px;
      bottom: 10px;
      z-index: 2;
      max-width: calc(100% - 20px);
      padding: 5px 10px;
      background: rgba(255, 255, 255, 0.94);
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(29, 30, 32, 0.12);
      line-height: 1.2;
      text-align: right;
    }
    #${overlayId} .map-scale-fallback {
      font-size: 7.5pt;
      color: #6e7376;
    }
    #${overlayId} .map-img {
      display: block;
      width: 100%;
      height: auto;
    }
    #${overlayId} .map-placeholder {
      padding: 24px;
      font-size: 9pt;
      color: #393b3d;
      text-align: center;
    }
    #${overlayId} .permitting-print-legend {
      font-size: 8pt;
      color: #393b3d;
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 8px;
      align-items: start;
    }
    #${overlayId} .legend-unavailable {
      margin: 0;
      font-size: 8pt;
      color: #6e7376;
      grid-column: 1 / -1;
    }
    #${overlayId} .legend-layer-block {
      margin-bottom: 0;
    }
    #${overlayId} .legend-block-title {
      font-weight: 600;
      color: #243442;
      margin-bottom: 2px;
      font-size: 8.5pt;
    }
    #${overlayId} .legend-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      margin: 2px 0;
    }
    #${overlayId} .legend-thumb {
      display: block;
      flex-shrink: 0;
      object-fit: contain;
      width: 16px;
      height: 16px;
      max-width: 16px;
      max-height: 16px;
    }
    #${overlayId} .legend-row-label {
      font-size: 8pt;
      line-height: 1.25;
    }
    #${overlayId} .legend-swatch {
      display: inline-block;
      width: 16px;
      height: 12px;
      flex-shrink: 0;
      box-sizing: border-box;
    }
    #${overlayId} .legend-swatch-project {
      border: 2px dashed ${PROJECT_AREA_LINE_COLOR};
      background: transparent;
    }
    #${overlayId} .map-scale-overlay .print-scale-host {
      display: inline-block;
    }
    #${overlayId} .print-scale-host .mapboxgl-ctrl-scale {
      position: static !important;
      display: inline-block !important;
      margin: 0 !important;
      bottom: auto !important;
      left: auto !important;
      transform: none !important;
      font-size: 8pt !important;
      color: #393b3d !important;
      border-color: #393b3d !important;
      box-shadow: none !important;
      line-height: 1.2 !important;
    }
    #${overlayId} .footer-print {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
      padding-top: 4px;
    }
    #${overlayId} .footer-left,
    #${overlayId} .footer-right {
      font-size: 7pt;
      font-weight: 500;
      color: #393b3d;
    }

    #${overlayId} .restoration-plan-page {
      box-sizing: border-box;
      font-family: "Source Sans 3", "Source Sans Pro", "Open Sans", system-ui, sans-serif;
      color: #000;
      font-weight: 400;
      line-height: 1.4;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0.85in 0.82in 0.5in;
      background: #fff;
      display: flex;
      flex-direction: column;
    }
    #${overlayId} .restoration-plan-footer {
      margin-top: auto;
      padding-top: 12px;
    }

    #${overlayId} .permitting-legend-page {
      box-sizing: border-box;
      width: 100%;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 14px 18px 12px;
      background: #fff;
      display: flex;
      flex-direction: column;
    }
    #${overlayId} .permitting-legend-page-content {
      flex: 1 1 auto;
    }
    #${overlayId} .permitting-legend-page-footer {
      margin-top: auto;
      padding-top: 12px;
    }

    #${overlayId} .permitting-map-page {
      box-sizing: border-box;
      width: 100%;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 14px 18px 12px;
      background: #fff;
      display: flex;
      flex-direction: column;
    }
    #${overlayId} .permitting-map-page-footer {
      margin-top: auto;
      padding-top: 12px;
    }

    @page {
      size: letter portrait;
      margin: ${PRINT_PAGE_MARGIN};
    }

    @media print {
      #${overlayId} .restoration-plan-page,
      #${overlayId} .permitting-legend-page,
      #${overlayId} .permitting-map-page {
        break-before: page;
        page-break-before: always;
      }
      /* The height stays definite: the map section inside is a flex: 1 1 0 child and needs a
         definite container height to shrink into, otherwise it keeps its natural size and pushes
         the page onto a second sheet. Safety against a shorter-than-expected printable area comes
         from the slack built into the page-box constants, not from letting the box grow. */
      #${overlayId} .permitting-legend-page,
      #${overlayId} .permitting-map-page {
        width: 100%;
        max-width: ${PRINT_PAGE_CONTENT_WIDTH};
        height: ${PRINT_PAGE_CONTENT_HEIGHT};
        margin: 0 auto;
        padding: 8px 12px 6px;
      }
      /* Padding is trimmed from the on-screen 0.85in/0.82in so the text column keeps roughly the
         measure it had when the 8.5in-wide box was being shrunk to fit by Chrome. */
      #${overlayId} .restoration-plan-page {
        width: 100%;
        max-width: ${PRINT_PAGE_CONTENT_WIDTH};
        height: ${PRINT_PAGE_CONTENT_HEIGHT};
        margin: 0 auto;
        padding: 0.6in 0.5in 0.35in;
      }
      #${overlayId} .permitting-map-page .map-section {
        flex: 1 1 0;
        min-height: 0;
      }
      #${overlayId} .contractors-print {
        gap: 3px;
      }
      #${overlayId} .contractor-row {
        gap: 2px 9px;
        padding: 3px 5px;
      }
      #${overlayId} .contractor-cell-label {
        font-size: 6pt;
      }
      #${overlayId} .contractor-cell-value {
        font-size: 7.5pt;
      }
      #${overlayId} .permitting-legend-page-footer {
        margin-top: auto;
        padding-top: 0;
      }
      /* No page-break-after: avoid here — it contradicts the forced break-before on the section
         that follows. Chrome resolves that conflict in favour of the forced break; WebKit can let
         the avoid win and suppress the break, collapsing the following page into this one. */
      #${overlayId} .letter {
        box-sizing: border-box;
        width: 100%;
        max-width: ${PRINT_PAGE_CONTENT_WIDTH};
        height: ${PRINT_PAGE_CONTENT_HEIGHT};
        margin: 0 auto;
        padding: 8px 12px 6px;
        border: none;
        display: flex;
        flex-direction: column;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      #${overlayId} .print-form-card {
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        padding: 12px;
        border: none;
        border-radius: 0;
        box-shadow: none;
      }
      #${overlayId} .print-permit-area-section {
        flex: 1 1 0;
        min-height: 0;
        margin-top: 18px;
        padding-top: 18px;
        border-top: none;
        gap: 8px;
      }
      #${overlayId} .print-subsection-title {
        font-size: 9pt;
      }
      #${overlayId} .print-subsection-hint {
        font-size: 7.5pt;
      }
      #${overlayId} .form-grid {
        flex: 0 0 auto;
        gap: 1px 11px;
      }
      #${overlayId} .permitting-construction-block {
        gap: 5px;
      }
      #${overlayId} .permitting-construction-row-rep-contractor,
      #${overlayId} .permitting-project-work-type-row,
      #${overlayId} .permitting-construction-row-third {
        gap: 5px 11px;
      }
      #${overlayId} .doc-title {
        margin: 3px 0;
        font-size: 11.5pt;
      }
      #${overlayId} .section-title {
        margin: 3px 0 2px;
        padding: 0;
        font-size: 8pt;
      }
      #${overlayId} .field-label {
        font-size: 7pt;
        margin-bottom: 2px;
      }
      #${overlayId} .field-box {
        min-height: 14px;
        padding: 3px 6px;
        font-size: 7.5pt;
        border-radius: 2px;
      }
      #${overlayId} .radio-line {
        gap: 7px 12px;
        padding: 3px 0;
      }
      #${overlayId} .radio-item {
        font-size: 7pt;
        gap: 4px;
      }
      #${overlayId} .radio-dot {
        width: 7px;
        height: 7px;
      }
      #${overlayId} .map-section {
        flex: 1 1 0;
        min-height: 0;
        margin-top: 0;
        margin-bottom: 0;
        width: 100%;
        padding-top: 0;
        padding-bottom: 0;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      /* The frame keeps its natural (image aspect) height when it fits, but must be allowed to
         shrink: the snapshot's aspect ratio is whatever the map viewport was, and a tall one would
         otherwise push its page onto a second sheet. flex-shrink + min-height: 0 cap the frame at
         the space available, object-fit scales the image into it, and the base rule's
         overflow: hidden still contains the image if a UA declines to resolve that percentage. */
      #${overlayId} .map-frame {
        position: relative;
        flex: 0 1 auto;
        align-self: stretch;
        width: 100%;
        height: auto;
        min-height: 0;
        border: 1px solid #d2d4d7;
        border-radius: 4px;
        background: #fff;
      }
      #${overlayId} .map-img {
        height: 100%;
        object-fit: contain;
      }
      #${overlayId} .map-north-overlay {
        left: 5px;
        top: 5px;
        padding: 2px 3px;
      }
      #${overlayId} .map-legend-below {
        margin-top: 6px;
        padding: 6px 8px;
      }
      #${overlayId} .map-scale-overlay {
        right: 6px;
        bottom: 6px;
        padding: 3px 6px;
      }
      #${overlayId} .map-placeholder {
        padding: 12px;
        font-size: 7.5pt;
        background: #fff;
      }
      #${overlayId} .permitting-print-legend,
      #${overlayId} .legend-row-label {
        font-size: 6.5pt;
      }
      #${overlayId} .permitting-print-legend {
        gap: 6px;
      }
      #${overlayId} .legend-row {
        gap: 5px;
        margin: 1px 0;
      }
      #${overlayId} .legend-block-title {
        font-size: 7pt;
        margin-bottom: 2px;
      }
      #${overlayId} .print-scale-host .mapboxgl-ctrl-scale {
        font-size: 6.5pt !important;
      }
      #${overlayId} .footer-print {
        flex-shrink: 0;
        margin-top: 0px;
        padding-top: 0px;
      }
      #${overlayId} .footer-left,
      #${overlayId} .footer-right {
        font-size: 6.5pt;
      }
      /* The page box now has a height, so margin-top: auto pins the footer to the bottom of the
         sheet like every other page footer — the old 82px spacer was standing in for that. */
      #${overlayId} .restoration-plan-footer {
        margin-top: auto;
        padding-top: 0;
      }
      #${overlayId} .restoration-plan-title {
        margin: 0 0 16px;
        font-size: 24pt;
        line-height: 1.15;
        font-weight: 500;
        text-align: center;
      }
      #${overlayId} .restoration-plan-heading {
        margin: 16px 0 10px;
        font-size: 20pt;
        line-height: 1.2;
      }
      #${overlayId} .restoration-plan-body {
        margin: 0 0 16px;
        font-size: 12pt;
        line-height: 1.4;
      }
    }
  `;
}
